/**
 * The ONE way a screen turns a failed Clerk call into words. (Citizen first run
 * · D10)
 *
 * `classifyClerkError` (`@tonnie/foundation`) reads Clerk's machine code as a
 * kind; the kind picks translated copy through `AUTH_ERROR_KEY`. A code the
 * table does not know gets the caller's `fallback` — the screen's own
 * translated "that did not work" — never Clerk's English text.
 *
 * `expected` says whether this is a refusal the person caused and can fix (a
 * wrong code, a taken email): the caller reports only the others, so Sentry
 * hears about the unknown — which is how a new Clerk code gets its own copy —
 * and not about ordinary typos.
 */
import { useCallback } from 'react';
import { classifyClerkError } from '@tonnie/foundation';

import { useTranslation } from './useTranslation';
import { AUTH_ERROR_KEY } from '../utils/auth-error-keys';

export interface AuthErrorMessage {
  /** Translated copy, ready to render. */
  message: string;
  /** Clerk's machine code (`'unknown'` without one) — for analytics, never for display. */
  code: string;
  /** A refusal with its own copy: the person can act on it, and it is not an incident. */
  expected: boolean;
}

export type AuthErrorMessageFn = (err: unknown, fallback: string) => AuthErrorMessage;

export function useAuthErrorMessage(): AuthErrorMessageFn {
  const { t } = useTranslation();
  return useCallback<AuthErrorMessageFn>(
    (err, fallback) => {
      const { kind, code } = classifyClerkError(err);
      return kind === null
        ? { message: fallback, code, expected: false }
        : { message: t(AUTH_ERROR_KEY[kind]), code, expected: true };
    },
    [t]
  );
}
